type PathElem = string | number;
type Obj = Record<string, any>;

export function get(
    obj: Obj | undefined | null,
    rawPath: string | PathElem[] = '',
    defaultValue: any = undefined,
): any {
    let path: PathElem[] = Array.isArray(rawPath) ? rawPath : [rawPath];
    path = path.flatMap(parsePath);

    let curr = obj;
    for (const key of path) {
        if (!curr) return curr;
        switch (typeof key) {
            case 'number':
                if (!Array.isArray(curr)) {
                    return defaultValue;
                }

                curr = curr[key];
                break;
            case 'string':
                if (typeof curr !== 'object' || Array.isArray(curr)) {
                    return defaultValue;
                }

                curr = curr[key];
                break;
            default:
                throw new Error(
                    `set expects path elements to be number or string, got ${typeof key}`,
                );
        }
    }

    if (defaultValue === undefined) return curr;
    return curr || defaultValue;
}

export function set(
    obj: Obj | null | undefined,
    rawPath: string | PathElem[] = '',
    value: any,
): Obj | null | undefined {
    let path: PathElem[] = Array.isArray(rawPath) ? rawPath : [rawPath];
    path = path.flatMap(parsePath);

    let curr: Obj | null | undefined = obj;
    for (let i = 0, len = path.length; i < len; i++) {
        const key = path[i];
        if (curr === undefined || curr === null) {
            return obj;
        }
        switch (typeof key) {
            case 'number':
                if (!Array.isArray(curr)) {
                    throw new Error(`expecting array, got ${typeof curr}`);
                }
                break;
            case 'string':
                if (typeof curr !== 'object' || Array.isArray(curr)) {
                    throw new Error(`expecting object, got ${typeof curr}`);
                }
                break;
            default:
                throw new Error(
                    `set expects path elements to be number or string, got ${typeof key}`,
                );
        }

        if (len === i + 1) {
            (curr as Obj)[key] = value;
        } else {
            if ((curr as Obj)[key] === null || (curr as Obj)[key] === undefined) {
                switch (typeof path[i + 1]) {
                    case 'number':
                        (curr as Obj)[key] = [];
                        break;
                    case 'string':
                        (curr as Obj)[key] = {};
                        break;
                }
            }
            curr = (curr as Obj)[key] as Obj | null | undefined;
        }
    }

    return obj;
}

export function parsePath(path: any): PathElem[] {
    if (typeof path !== 'string') {
        return path;
    }
    return path
        .split(/[.[\]]/)
        .filter((i) => i)
        .map((i) => {
            const n = Number(i);
            return isNaN(n) ? i : n;
        });
}

export function deepMerge<T extends Record<string, any>, U extends Record<string, any>>(
    target: T,
    source: U,
): T & U {
    const result: Record<string, any> = { ...target }; // Create a shallow copy of target

    for (const key of Object.keys(source)) {
        if (
            typeof source[key] === 'object' &&
            source[key] !== null &&
            !Array.isArray(source[key]) &&
            typeof target[key] === 'object' &&
            target[key] !== null &&
            !Array.isArray(target[key])
        ) {
            // Recursively merge objects
            result[key] = deepMerge(target[key], source[key]);
        } else {
            // Assign non-object values (including arrays, functions, etc.)
            result[key] = source[key];
        }
    }

    return result as T & U;
}

// excludes properties with undefined values
export function deepClean<T>(obj: T): T {
    if (Array.isArray(obj)) {
        return obj.map((item) => deepClean(item)).filter((item) => item !== undefined) as T;
    } else if (typeof obj === 'object' && obj !== null) {
        const result: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(obj)) {
            const cleanedValue = deepClean(value);
            if (cleanedValue !== undefined) {
                result[key] = cleanedValue;
            }
        }

        return result as T;
    }
    return obj;
}
